import { isValidNumber } from './number';
import { MICRO_CONVERSION } from './money';
import { toFixedDecimalPercentage } from './percentage';
import { VariantPackCostType } from '@/types';
import type { SalesRepInfoCostMap } from '@/domain/pricing/types';

/**
 * Determines the cost of a variant pack in micros based on a provided cost map
 * and the configured price editing cost type. The logic prioritizes the
 * "Deviated Cost" as the definitive source of truth whenever it is present.
 *
 * Example `costMap`:
 * {
 *   SALES_COST: 0,
 *   FLOOR_COST: 10000,
 *   DEVIATED_COST: 35110000
 * }
 *
 * @param costMap - A mapping of cost types to their corresponding values in micros
 * @param customerCostType - The cost type to use if `DEVIATED_COST` is not present
 * @returns The cost in micros, or `null` if no applicable cost is found
 */
export const getVariantPackCostFromMap = (
  costMap: SalesRepInfoCostMap,
  customerCostType: VariantPackCostType | null,
) => {
  return costMap[VariantPackCostType.DeviatedCost]
    ? costMap[VariantPackCostType.DeviatedCost]
    : customerCostType
      ? costMap[customerCostType]
      : null;
};

/**
 * Formats a gross profit percentage for display.
 * @param grossProfitPercent Gross profit as percentage (25 for 25%)
 * @returns Formatted percentage string without % symbol
 */
export const formatGrossProfitDisplay = (grossProfitPercent: number | null) => {
  if (!isValidNumber(grossProfitPercent)) {
    return '';
  }
  return toFixedDecimalPercentage(grossProfitPercent).replace('%', '');
};

export const formatPriceDisplayFromMicros = (priceInMicros: number | null) => {
  if (!isValidNumber(priceInMicros)) {
    return '';
  }
  const priceInDollars = priceInMicros / MICRO_CONVERSION;
  return priceInDollars.toFixed(2);
};

/**
 * Prepares gross profit percentage for database save.
 * Rounds to 6 decimal places for precision.
 * @param grossProfitPercent Gross profit percentage
 * @returns Rounded value ready for save
 */
export const formatGrossProfitForSave = (grossProfitPercent: number | null) => {
  if (!isValidNumber(grossProfitPercent) || grossProfitPercent === 0) {
    return null;
  }

  // Round to 6 decimal places
  return Math.round(grossProfitPercent * 1000000) / 1000000;
};

/**
 * Prepares price for database save.
 * Rounds to nearest integer for precision.
 * @param priceInMicros Price in micros
 * @returns Rounded value ready for save
 */
export const formatPriceForSave = (priceInMicros: number | null) => {
  if (!isValidNumber(priceInMicros)) {
    return null;
  }

  // Round to integer
  return Math.round(priceInMicros);
};
